import React from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaFileCsv, FaFileExcel, FaFileDownload } from 'react-icons/fa';

export type ExportFormat = 'csv' | 'xlsx';

export type ExportOption = {
  value: ExportFormat;
  label: string;
  icon: React.ReactNode;
}

export type FileExportMenuProps = {
  isDisabled?: boolean;
  allowedFormats: ExportFormat[];
  handleExport: (format: ExportFormat, option: ExportOption) => void;
  label?: React.ReactNode;
}

const EXPORT_FORMATS: Record<ExportFormat, Omit<ExportOption, 'label'>> = {
  csv: {
    value: 'csv',
    icon: <FaFileCsv className="me-3" />
  },
  xlsx: {
    value: 'xlsx',
    icon: <FaFileExcel className="me-3" />
  },
};



export const FileExportMenu: React.FC<FileExportMenuProps> = ({
  isDisabled = false,
  allowedFormats,
  handleExport,
  label,
}) => {
  const { t } = useTranslation();

  const TRANSLATION_KEYS: Record<ExportFormat, { key: string; fallback: string }> = {
    csv: { key: t('export_as_csv'), fallback: 'Export as CSV' },
    xlsx: { key: t('export_as_excel'), fallback: 'Export as Excel' },
  };

  const exportOptions: ExportOption[] = allowedFormats
    .filter(format => EXPORT_FORMATS[format])
    .map(format => ({
      ...EXPORT_FORMATS[format],
      label: TRANSLATION_KEYS[format].key || TRANSLATION_KEYS[format].fallback
    }));

  const handleOptionClick = (option: ExportOption): void => {
    handleExport(option.value, option);
  };

  if (exportOptions.length === 0) {
    return null;
  }

  return (
    <Dropdown className="ms-1 align-text-center d-inline-block action-dropdown">

      <Dropdown.Toggle disabled={isDisabled} className="me-3 d-flex align-items-center justify-content-center btn m-0">
        <FaFileDownload className="fs-5 me-2" style={{ width: 20, height: 20 }} />
        {label ?? t("download", "Download")}
      </Dropdown.Toggle>

      <Dropdown.Menu className="position-fixed">
        {exportOptions.map((option) => (
          <Dropdown.Item
            key={option.value}
            onClick={() => handleOptionClick(option)}
            className="d-flex align-items-center"
          >
            {option.icon}
            {option.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>

    </Dropdown>

  );
};
